/**
 * Turning a stored answer field into something a receptionist reads.
 *
 * The session document is written by the patient's client, so its keys are
 * camelCase field names and its values are the enum strings and booleans the
 * elements save: `idType: "national-id"`, `hasReferral: false`. Printed straight
 * out that is programmer language on a clinical screen, and "has referral: false"
 * is worse than useless — a "false" is read as a value someone entered rather
 * than as "no".
 *
 * Derived rather than mapped per element. A label dictionary keyed by field name
 * would be a second place to update every time an element gains a field, and the
 * failure mode is silent: the new field would just print raw. These rules cover
 * the shapes the catalog actually saves, and anything unrecognised degrades to
 * spaced-out sentence case rather than to nothing.
 */

import { format, parseISO } from "date-fns";

/**
 * Words that are acronyms in this domain and look wrong in sentence case. Applied
 * after the generic rules, so "idNumber" becomes "ID number" rather than
 * "Id number".
 */
const ACRONYMS = {
  id: "ID",
  sha: "SHA",
  nhif: "NHIF",
  ncd: "NCD",
  tb: "TB",
  hiv: "HIV",
  art: "ART",
  dob: "DOB",
};

/**
 * Stored values whose human form is not just the string tidied up. Everything
 * else goes through the generic rule, which is why this stays short.
 */
const VALUE_LABELS = {
  "national-id": "National ID",
  "huduma-namba": "Huduma Namba",
  "birth-certificate": "Birth certificate",
  passport: "Passport",
  none: "None",
  not_sure: "Not sure",
  "not-sure": "Not sure",
  prefer_not_to_answer: "Preferred not to answer",
  "prefer-not-to-answer": "Preferred not to answer",
};

const fixAcronyms = (text) =>
  text
    .split(" ")
    .map((word) => ACRONYMS[word.toLowerCase()] || word)
    .join(" ");

const sentenceCase = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/** ISO dates are what the date elements store; nobody reads 2004-01-01 out loud. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A stored field name as a label: `dateOfBirth` → "Date of birth", `idNumber` →
 * "ID number", `hasReferral` → "Has referral".
 */
export function answerFieldLabel(key) {
  const spaced = String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim()
    .toLowerCase();
  return sentenceCase(fixAcronyms(spaced));
}

/**
 * A stored value as a reader sees it. Booleans are the important case: they are
 * how every yes/no element saves its answer.
 */
export function answerValueLabel(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return String(value);

  const raw = String(value).trim();
  if (!raw) return "";

  const known = VALUE_LABELS[raw.toLowerCase()];
  if (known) return known;

  if (ISO_DATE.test(raw)) {
    // Guarded: a well-shaped string can still be an impossible date, and an
    // Invalid Date printed to a clinical screen is worse than the ISO string.
    const parsed = parseISO(raw);
    if (!Number.isNaN(parsed.getTime())) return format(parsed, "d MMM yyyy");
    return raw;
  }

  // Only what LOOKS like a stored value gets tidied: all lower case, no
  // punctuation but the separators. Anything carrying a capital was typed by a
  // person — an insurance reference like "SHA-99X", a facility name — and its own
  // capitalisation is data, so an earlier version of this rule that keyed off the
  // presence of a hyphen turned that reference into "SHA 99X".
  //
  // A lower-case free-text answer containing a real hyphen would lose it, which
  // is a trade this makes knowingly: the flat shapes this branch renders hold
  // enums and short identifiers, and free text (the interview, medications,
  // allergies) is rendered by the branches above.
  if (/^[a-z0-9]+([-_ ][a-z0-9]+)*$/.test(raw)) {
    return sentenceCase(fixAcronyms(raw.replace(/[-_]+/g, " ")));
  }
  return raw;
}
